// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Archivos Adjuntos Panel
// Displays attached files for a document (storage + external links)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { C } from '../../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import {
  fetchArchivos,
  subirArchivo,
  registrarEnlaceExterno,
  eliminarArchivo,
  obtenerUrlArchivo,
} from '../../../../shared/services/api';
import type { ArchivoAdjunto } from '../../../../../types';

// ─── Props ───

interface ArchivosPanelProps {
  codigoDocumento: string;
  readOnly?: boolean;
}

// ─── MIME icon helper ───

function mimeIcon(mime: string): string {
  if (mime.startsWith('image/')) return '\uD83D\uDDBC\uFE0F';
  if (mime.startsWith('video/')) return '\uD83C\uDFA5';
  if (mime.startsWith('audio/')) return '\uD83C\uDFB5';
  if (mime.includes('pdf')) return '\uD83D\uDCC4';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return '\uD83D\uDCCA';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '\uD83D\uDCCA';
  if (mime.includes('word') || mime.includes('document')) return '\uD83D\uDCC3';
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar'))
    return '\uD83D\uDCE6';
  return '\uD83D\uDCCE';
}

// ─── Format file size ───

function formatSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Styles ───

const ACCENT = '#0E243E';

const TH_STYLE: React.CSSProperties = {
  backgroundColor: ACCENT,
  color: '#fff',
  fontWeight: 700,
  fontSize: 11,
  padding: '10px 14px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
};

const TD_STYLE: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  color: C.text,
  whiteSpace: 'nowrap',
};

const BTN: React.CSSProperties = {
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  background: '#fff',
  color: ACCENT,
  cursor: 'pointer',
  transition: 'all 0.15s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const BTN_PRIMARY: React.CSSProperties = {
  ...BTN,
  background: ACCENT,
  color: '#fff',
  border: `1px solid ${ACCENT}`,
};

const INPUT_STYLE: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${C.border}`,
  fontSize: 13,
  fontFamily: 'inherit',
  width: '100%',
};

// ─── Component ───

export function ArchivosPanel({ codigoDocumento, readOnly }: ArchivosPanelProps) {
  const { t } = useI18n();
  const [archivos, setArchivos] = useState<ArchivoAdjunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showEnlaceForm, setShowEnlaceForm] = useState(false);
  const [enlaceUrl, setEnlaceUrl] = useState('');
  const [enlaceNombre, setEnlaceNombre] = useState('');
  const [enlaceMime, setEnlaceMime] = useState('');
  const [enlaceDesc, setEnlaceDesc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadArchivos = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchArchivos(codigoDocumento)
      .then((r) => setArchivos(r.data ?? []))
      .catch((e) => setError(e?.message ?? 'Error'))
      .finally(() => setLoading(false));
  }, [codigoDocumento]);

  useEffect(() => {
    loadArchivos();
  }, [loadArchivos]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      setError(null);
      try {
        await subirArchivo(codigoDocumento, file);
        setToast(t.archivos.subido);
        loadArchivos();
      } catch (err: any) {
        setError(err?.message ?? t.archivos.errorSubir);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [codigoDocumento, loadArchivos, t],
  );

  const handleEnlace = useCallback(async () => {
    if (!enlaceUrl || !enlaceNombre) return;
    setError(null);
    try {
      await registrarEnlaceExterno(codigoDocumento, {
        nombreArchivo: enlaceNombre,
        enlaceExterno: enlaceUrl,
        mimeType: enlaceMime || undefined,
        descripcion: enlaceDesc || undefined,
      });
      setToast(t.archivos.enlaceRegistrado);
      setShowEnlaceForm(false);
      setEnlaceUrl('');
      setEnlaceNombre('');
      setEnlaceMime('');
      setEnlaceDesc('');
      loadArchivos();
    } catch (err: any) {
      setError(err?.message ?? 'Error');
    }
  }, [codigoDocumento, enlaceUrl, enlaceNombre, enlaceMime, enlaceDesc, loadArchivos, t]);

  const handleDelete = useCallback(
    async (archivo: ArchivoAdjunto) => {
      if (!confirm(t.archivos.confirmarEliminar)) return;
      try {
        await eliminarArchivo(codigoDocumento, archivo.id);
        loadArchivos();
      } catch (err: any) {
        setError(err?.message ?? 'Error');
      }
    },
    [codigoDocumento, loadArchivos, t],
  );

  const handleOpen = useCallback(
    async (archivo: ArchivoAdjunto) => {
      if (archivo.enlaceExterno) {
        window.open(archivo.enlaceExterno, '_blank', 'noopener');
        return;
      }
      try {
        const res = await obtenerUrlArchivo(codigoDocumento, archivo.id);
        const url = res.data.url;
        if (archivo.modoApertura === 'DESCARGA') {
          const a = document.createElement('a');
          a.href = url;
          a.download = archivo.nombreArchivo;
          a.click();
        } else {
          window.open(url, '_blank', 'noopener');
        }
      } catch (err: any) {
        setError(err?.message ?? 'Error');
      }
    },
    [codigoDocumento],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Toolbar */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <button
            style={BTN_PRIMARY}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Ic d={ICON_PATHS.plus} s={14} c="#fff" />
            {uploading ? t.archivos.subiendo : t.archivos.subir}
          </button>
          <button style={BTN} onClick={() => setShowEnlaceForm((f) => !f)}>
            <Ic d={ICON_PATHS.link} s={14} c={ACCENT} />
            {t.archivos.enlace}
          </button>
        </div>
      )}

      {/* Enlace form */}
      {showEnlaceForm && !readOnly && (
        <div
          style={{
            padding: 16,
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: C.bg,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textSec,
                  marginBottom: 2,
                  display: 'block',
                }}
              >
                {t.archivos.urlEnlace} *
              </label>
              <input
                style={INPUT_STYLE}
                value={enlaceUrl}
                onChange={(e) => setEnlaceUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textSec,
                  marginBottom: 2,
                  display: 'block',
                }}
              >
                {t.archivos.nombreArchivo} *
              </label>
              <input
                style={INPUT_STYLE}
                value={enlaceNombre}
                onChange={(e) => setEnlaceNombre(e.target.value)}
                placeholder={t.archivos.nombreArchivo}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textSec,
                  marginBottom: 2,
                  display: 'block',
                }}
              >
                {t.archivos.tipoMime}
              </label>
              <input
                style={INPUT_STYLE}
                value={enlaceMime}
                onChange={(e) => setEnlaceMime(e.target.value)}
                placeholder="application/pdf"
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.textSec,
                  marginBottom: 2,
                  display: 'block',
                }}
              >
                {t.archivos.descripcion}
              </label>
              <input
                style={INPUT_STYLE}
                value={enlaceDesc}
                onChange={(e) => setEnlaceDesc(e.target.value)}
                placeholder={t.archivos.descripcion}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={BTN_PRIMARY}
              onClick={handleEnlace}
              disabled={!enlaceUrl || !enlaceNombre}
            >
              {t.archivos.enlace}
            </button>
            <button style={BTN} onClick={() => setShowEnlaceForm(false)}>
              <Ic d={ICON_PATHS.x} s={14} c={ACCENT} />
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FEF2F2',
            color: '#DC2626',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.textSec, fontSize: 13 }}>...</div>
      ) : archivos.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.textMut, fontSize: 13 }}>
          {t.archivos.sinArchivos}
        </div>
      ) : (
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            overflow: 'hidden',
            border: `1px solid ${C.border}`,
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH_STYLE, width: 36 }}></th>
                <th style={TH_STYLE}>{t.archivos.nombreArchivo}</th>
                <th style={{ ...TH_STYLE, width: 90 }}>{t.archivos.tipoMime}</th>
                <th style={{ ...TH_STYLE, width: 80 }}></th>
                <th style={{ ...TH_STYLE, width: 80, textAlign: 'right' }}>KB</th>
                <th style={{ ...TH_STYLE, width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {archivos.map((archivo, idx) => (
                <tr
                  key={archivo.id}
                  style={{
                    backgroundColor: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                    borderBottom: '1px solid #F0F0F0',
                  }}
                >
                  <td style={{ ...TD_STYLE, textAlign: 'center', fontSize: 16 }}>
                    {mimeIcon(archivo.mimeType)}
                  </td>
                  <td style={{ ...TD_STYLE, fontWeight: 500, maxWidth: 240 }}>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {archivo.nombreArchivo}
                    </span>
                    {archivo.descripcion && (
                      <span style={{ fontSize: 11, color: C.textMut, display: 'block' }}>
                        {archivo.descripcion}
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD_STYLE, fontSize: 11, color: C.textSec }}>
                    {archivo.mimeType}
                  </td>
                  <td style={TD_STYLE}>
                    {archivo.enlaceExterno ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: '#16A34A',
                          backgroundColor: '#F0FDF4',
                          border: '1px solid #16A34A25',
                        }}
                      >
                        {t.archivos.enlaceExterno}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          color: '#2563EB',
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #2563EB25',
                        }}
                      >
                        {t.archivos.storage}
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD_STYLE, textAlign: 'right', fontSize: 12, color: C.textSec }}>
                    {formatSize(archivo.tamanoBytes)}
                  </td>
                  <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        style={{ ...BTN, padding: '4px 8px', fontSize: 11 }}
                        onClick={() => handleOpen(archivo)}
                        title={archivo.modoApertura === 'DESCARGA' ? 'Download' : 'Preview'}
                      >
                        <Ic
                          d={
                            archivo.modoApertura === 'DESCARGA'
                              ? ICON_PATHS.download
                              : ICON_PATHS.eye
                          }
                          s={14}
                          c={ACCENT}
                        />
                      </button>
                      {!readOnly && (
                        <button
                          style={{
                            ...BTN,
                            padding: '4px 8px',
                            fontSize: 11,
                            borderColor: '#EF444440',
                            color: '#EF4444',
                          }}
                          onClick={() => handleDelete(archivo)}
                          title={t.archivos.eliminar}
                        >
                          <Ic d={ICON_PATHS.x} s={14} c="#EF4444" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 2000,
            padding: '12px 20px',
            borderRadius: 10,
            backgroundColor: ACCENT,
            color: '#fff',
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Ic d={ICON_PATHS.check} s={16} c="#fff" />
          {toast}
        </div>
      )}
    </div>
  );
}
